import React from "react";
import ReactDOM from "react-dom";
import { graphql } from "react-relay";

import { faArrowFromLeft, faEdit, faSearch, faStar } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faHashtag } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { icon } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createPopper, type Modifier, type Options } from "@popperjs/core";
import cx from "classnames";
import { compact } from "lodash";
import { type DOMOutputSpec, Fragment, type NodeSpec, Node as ProseMirrorNode, Schema } from "prosemirror-model";
import { EditorState, NodeSelection, Selection, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView, type NodeView } from "prosemirror-view";
import { commitMutation, fetchQuery } from "relay-runtime";
import uuid from "uuid";

import { zindexMenu } from "~/css/zindexes";
import history from "~/lib/history";
import { getContext } from "~/lib/prose/plugins/context";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";
import { environment } from "~/models";
import {
	determineGroupIcon,
	determineGroupName,
	willGroupAcceptObject,
} from "~/nav/panels/Home/Sections/HomePanelGroup";
import type { ModalData } from "~/stream/CreateEditGroupModal";
import CreateEditGroupModal from "~/stream/CreateEditGroupModal";
import { CreateGroupMutation } from "~/stream/CreateGroupButton";
import type { Item as ItemType } from "~/stream/HomePanelGroupMenuItem";
import { addOrMoveObjectToGroupMutation } from "~/stream/HomePanelGroupsMenu";
import { launchEditTagModal } from "~/tags";
import Menu from "~/ui/menu";
import { __globalModalContext } from "~/ui/modals";

import { NodeExtension } from "../extension";

import type { CreateGroupButtonMutation } from "~/__generated__/CreateGroupButtonMutation.graphql";
import type { HomePanelGroupType } from "~/__generated__/HomePanelGroup_group.graphql";
import type { HomePanelGroupsMenu_Mutation as HomePanelGroupsMenuMoveObjectMutation } from "~/__generated__/HomePanelGroupsMenu_Mutation.graphql";
import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";
import type { tagGroupsQuery } from "~/__generated__/tagGroupsQuery.graphql";

import tagStyles from "~/tags/TagComponent.module.css";

import styles from "./tag.module.css";

// these are copied in ~/components/tags/TagComponent.tsx. Importing caused a cyclic dependency
export const buildTagSearchUrl = (tagLabelWithHash: string) => {
	return `/search/?${new URLSearchParams({ q: `${tagLabelWithHash}` }).toString()}`;
};

function goToTagSearch(tag: { label: string }) {
	history.push(buildTagSearchUrl(`#${tag.label}`));
}

const TagGroupsQuery = graphql`
	query tagGroupsQuery($id: ID!) {
		node(id: $id) {
			... on Tag {
				id
				homePanelGroup {
					id
				}
			}
		}
		viewer {
			homePanelGroups {
				id
				type
				name
				emojiName
			}
		}
	}
`;

interface TagToolbarProps {
	tag: Viewer["account"]["tags"][number];
	url: string;
	hideToolbar: () => void;
	allowEditing: boolean;
}

type HomePanelTagMenuItem = readonly ({
	readonly id: string;
	readonly name: string;
	readonly emojiName: string | null;
	readonly type: HomePanelGroupType;
} | null)[];

function addObjectToGroup(groupId: string, objectId: string) {
	commitMutation<HomePanelGroupsMenuMoveObjectMutation>(environment, {
		mutation: addOrMoveObjectToGroupMutation,
		variables: { groupId: groupId, objectId: objectId },
	});
}

const TagToolbar: React.FC<TagToolbarProps> = ({ tag, url, hideToolbar, allowEditing }) => {
	const [groups, setGroups] = React.useState<HomePanelTagMenuItem | null>(null);
	const [groupId, setGroupId] = React.useState<string | undefined>(undefined);

	React.useEffect(() => {
		fetchQuery<tagGroupsQuery>(environment, TagGroupsQuery, { id: tag.id }, { fetchPolicy: "store-or-network" })
			.toPromise()
			.then(response => {
				if (response) {
					setGroupId(response.node?.homePanelGroup?.id);
					setGroups(response.viewer.homePanelGroups);
				}
			});
	}, [tag.id]);

	const launchTagModal = React.useCallback(
		() => launchEditTagModal({ tagId: tag.id, startingLabel: tag.label, startingColor: tag.color }),
		[tag],
	);

	const createGroup = React.useCallback(async () => {
		hideToolbar();
		const result = await __globalModalContext?.open<ModalData>(({ close }) => <CreateEditGroupModal onClose={close} />);

		if (!result) return;

		commitMutation<CreateGroupButtonMutation>(environment, {
			mutation: CreateGroupMutation,
			variables: { name: result.name, emoji: result.emoji, objectId: tag.id },
		});
	}, [hideToolbar, tag.id]);

	const goToSearch = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			if (e.defaultPrevented || e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return;
			e.preventDefault();
			goToTagSearch(tag);
		},
		[tag],
	);

	const addToGroup = React.useCallback(
		(groupId: string) => {
			addObjectToGroup(groupId, tag.id);
			setGroupId(groupId);
		},
		[tag.id],
	);

	const hideToolbarCallback = React.useCallback(() => hideToolbar(), [hideToolbar]);

	return (
		<div className={styles.toolbar}>
			<a className={styles.toolbarItem} href={url} onClick={goToSearch}>
				<FontAwesomeIcon icon={faSearch} className={styles.toolbarItemIcon} fixedWidth />
				<span className={styles.toolbarItemText}>Search tag</span>
			</a>
			{allowEditing && (
				<button className={cx(styles.toolbarButton, styles.toolbarItem)} onClick={launchTagModal}>
					<FontAwesomeIcon icon={faEdit} className={styles.toolbarItemIcon} fixedWidth />
					<span className={styles.toolbarItemText}>Edit</span>
				</button>
			)}
			<div className={styles.separator} />
			<Menu>
				<Menu.Trigger>
					<button className={cx(styles.toolbarButton, styles.toolbarItem)}>
						<FontAwesomeIcon icon={faArrowFromLeft} className={styles.toolbarItemIcon} fixedWidth />
						<span className={styles.toolbarItemText}>{groupId ? "Move to group" : "Add to home panel"}</span>
					</button>
				</Menu.Trigger>
				<Menu.Content className={styles.subMenu} side="right" data-fellow-tag-group-menu>
					<Menu.Label>{groupId ? "Move to group" : "Add to group"}</Menu.Label>
					<Menu.RadioGroup value={groupId} onValueChange={addToGroup}>
						{compact(groups)
							.filter(group => willGroupAcceptObject({ type: group.type }, tag))
							.map(group => {
								const icon = determineGroupIcon(group, "light");

								let itemProps: ItemType = {};

								if (typeof icon === "string") {
									itemProps = { emoji: icon };
								} else {
									itemProps = { icon };
								}

								return (
									<Menu.RadioItem value={group.id} {...itemProps} key={group.id} onSelect={hideToolbarCallback}>
										{determineGroupName(group)}
									</Menu.RadioItem>
								);
							})}
					</Menu.RadioGroup>

					<Menu.Separator />
					<Menu.Item icon={faStar} onSelect={createGroup}>
						Create new group…
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
};

const zIndexModifier: Modifier<"zIndexModifier", Options> = {
	name: "zIndexModifier",
	enabled: true,
	phase: "main",
	fn({ state }) {
		const { styles } = state;

		styles["popper"]["zIndex"] = `${zindexMenu}`;
		return { ...state, styles: { ...styles } };
	},
};

export default class TagNode extends NodeExtension {
	showToolbar: boolean;
	allowEditing: boolean;
	clickable: boolean;
	toolbar: HTMLDivElement;
	toolbarTimeout: NodeJS.Timeout | null = null;

	constructor(options: {
		showToolbar: boolean;
		allowEditing?: boolean;
		/** If true, tags are clickable links. Gets overridden in some contexts, and should not be true for guests! */
		clickable: boolean;
	}) {
		super();

		this.showToolbar = options.showToolbar;
		this.allowEditing = !!options.allowEditing;
		// Clickability is not allowed in some embedded contexts
		this.clickable = options.clickable;
		this.toolbar = document.createElement("div");
	}

	get name() {
		return "tag";
	}

	private getUserTag(state: EditorState, labelWithoutHash: string): Viewer["account"]["tags"][number] | undefined {
		// TODO: Get types in context. Having this return `any` isn't very fun
		const context = getContext(state);
		return context?.tags?.find(({ label }: { label: string }) => label === labelWithoutHash);
	}

	view = (
		node: ProseMirrorNode,
		view: EditorView,
		_getPos: unknown,
		_decorations: readonly Decoration[],
		/**
		 * Neither the types nor the docs seem to give an accurate impression of what's in this argument 🤪
		 * The docs give almost no details about it, and the types don't mention it at all. I've filled out
		 * this type definition conservatively, based on `console.log`-ing values I've observed in testing.
		 * @see https://prosemirror.net/docs/ref/#view.DecorationSource
		 */
		innerDecorations?: DecorationSet & { local?: { type?: { attrs?: { class?: string } } }[] },
	): NodeView => {
		const labelString = node.textContent.slice(1);
		const userTag = this.getUserTag(view.state, labelString);
		const tagColor = userTag?.color || "NO_COLOR";
		const context = getContext(view.state);

		const dom = document.createElement("span");
		dom.classList.add(styles["prose-tag"]);
		dom.classList.add(styles[`prose-tag-${tagColor.toLowerCase()}`]);
		dom.classList.toggle(styles.clickable, this.clickable);
		dom.classList.add(styles.proseOverride);
		if (innerDecorations?.local?.some(d => d.type?.attrs?.class === "subheader")) {
			dom.classList.add("subheader");
		}
		dom.setAttribute("data-fellow-tag", "");
		dom.setAttribute("data-entity-id", node.attrs.entityId);

		const hashIconNode = icon(faHashtag, {
			classes: [tagStyles.hashIcon],
		}).node[0];

		const label = document.createElement("strong");
		label.innerText = labelString;

		const hideToolbar = () => {
			ReactDOM.unmountComponentAtNode(this.toolbar);
			this.toolbar.remove();
			this.toolbarTimeout = null;
		};

		const showToolbar = () => {
			if (!this.showToolbar) return;

			const userTag = this.getUserTag(view.state, labelString);
			if (!userTag) return hideToolbar();

			const reactEl = (
				<TagToolbar
					tag={userTag}
					url={buildTagSearchUrl(node.textContent)}
					// eslint-disable-next-line fellow/no-inline-disable
					// eslint-disable-next-line react/jsx-no-bind
					hideToolbar={hideToolbar}
					allowEditing={this.allowEditing}
				/>
			);

			// NOTE: Copied from link.tsx
			// "re-appending the toolbar DOM causes it to lose focus when someone else is typing"
			if (this.toolbar.parentElement != view.dom.parentElement) {
				(view.dom.parentElement as HTMLElement).appendChild(this.toolbar);
			}

			ReactDOM.unmountComponentAtNode(this.toolbar);
			ReactDOM.render(reactEl, this.toolbar);
			createPopper(dom, this.toolbar, {
				modifiers: [
					{
						name: "offset",
						options: {
							offset: [0, 4],
						},
					},
					zIndexModifier,
				],
			});
		};

		const checkElementClicked = (event: MouseEvent) => {
			if (event.target instanceof Element) {
				if (event.target.closest("[data-fellow-tag-group-menu]")) return;
			}

			if (event.target instanceof Node) {
				if (this.toolbar.contains(event.target)) return;
			}

			hideToolbar();
		};

		if (this.clickable && !inVideocallMode && !inCalendarMode && !context?.isTeams) {
			dom.addEventListener("click", showToolbar);
			document.body.addEventListener("click", checkElementClicked, true);
		}

		dom.appendChild(hashIconNode);
		dom.appendChild(label);

		return {
			dom,
			update: newNode => {
				if (newNode !== node) return false;

				const tagColor = this.getUserTag(view.state, labelString)?.color || "NO_COLOR";
				dom.classList.add(styles[`prose-tag-${tagColor.toLowerCase()}`]);

				label.innerText = node.textContent.slice(1);
				return true;
			},
			destroy: () => {
				document.body.removeEventListener("click", checkElementClicked);

				dom.remove();
			},
		};
	};

	get node(): NodeSpec {
		return {
			group: "inline",
			content: "text{0,1}",
			inline: true,
			atom: true,
			isolating: true,
			marks: "",
			attrs: {
				entityId: { default: "" },
			},
			selectable: true,
			draggable: false,
			toDOM: (node: ProseMirrorNode): DOMOutputSpec => {
				const hashIconNode = icon(faHashtag, {
					classes: [tagStyles.hashIcon],
				}).node[0];

				return [
					"span",
					{
						class: cx(styles["prose-tag"], styles[`prose-tag-no_color`], styles.proseOverride),
						"data-fellow-tag": "",
						"data-entity-id": node.attrs.entityId,
					},
					hashIconNode,
					["strong", node.textContent.slice(1)],
				];
			},
			parseDOM: [
				{
					tag: "[data-fellow-tag]",
					priority: 51, // default is 50, so this takes priority over default link nodes
					getAttrs(dom) {
						if (!(dom instanceof Element) || !dom.textContent) return false;
						return {
							entityId: uuid.v4(),
						};
					},
					getContent(node: Node, schema: Schema) {
						const contentNode = schema.text(`#${node.textContent}`) as ProseMirrorNode;

						return Fragment.from(contentNode);
					},
				},
			],
		};
	}

	keys() {
		const isTagSelected = (sel: Selection): sel is NodeSelection =>
			sel instanceof NodeSelection && sel.node.type.name === this.name;

		return {
			Enter: (state: EditorState) => {
				if (!isTagSelected(state.selection)) return false;

				const tagLabel = state.selection.node.textContent;
				if (!tagLabel) return false;

				const userTag = this.getUserTag(state, tagLabel.slice(1));

				if (!userTag) return false;

				goToTagSearch(userTag);
				return true;
			},
			ArrowLeft: (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				if (!dispatch) return false;
				if (!isTagSelected(state.selection)) return false;

				const { $from, from } = state.selection;

				// Check if the node before it is also a tag
				if ($from.nodeBefore?.type.name !== this.name) return false;

				const $nodePos = state.doc.resolve(from - $from.nodeBefore.nodeSize);
				const newNodeSelection = new NodeSelection($nodePos);

				dispatch(state.tr.setSelection(newNodeSelection));
				return true;
			},
			ArrowRight: (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				if (!dispatch) return false;
				if (!isTagSelected(state.selection)) return false;

				const { $to, to } = state.selection;

				// Check if the node after it is also a tag
				if ($to.nodeAfter?.type.name !== this.name) return false;

				const $nodePos = state.doc.resolve(to);
				const newNodeSelection = new NodeSelection($nodePos);

				dispatch(state.tr.setSelection(newNodeSelection));
				return true;
			},
			Space: (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				if (!dispatch) return false;
				if (!isTagSelected(state.selection)) return false;

				dispatch(state.tr.insertText(" ", state.selection.to));
				return true;
			},
		};
	}
}
